import {useCallback, useMemo} from 'react'
import {msg} from '@lingui/core/macro'
import {useLingui} from '@lingui/react'

import {useInterestsDisplayNames} from '#/lib/interests'
import {type TrendsScope} from '../components/TrendsScopeTabs'
import {type Trend, type TrendActivityStatus} from './trends'

/**
 * Green rank-pill colours for the Top trends block. Locked by design (mirrors
 * the `ACTIVITY_STATUS_COLORS` pattern): bright mint text on a translucent
 * green fill, both drawn from the `positive` palette.
 */
export const TREND_RANK_PILL = {
  text: '#11E8B2', // positive_300
  background: 'rgba(17, 232, 178, 0.12)',
} as const

/**
 * Builds the green rank-pill label for a Top trends card. Scope-derived:
 *   global → "#N Trend Globally"
 *   region → "#N Trend in {region}"  (region = Sweden / Europe / country name)
 * Region with no resolved name falls back to the generic "#N Trend".
 */
export function useTrendRankLabel() {
  const {_} = useLingui()
  return useCallback(
    (rank: number, scope: TrendsScope, regionName?: string): string => {
      if (scope === 'global') return _(msg`#${rank} Trend Globally`)
      if (regionName) return _(msg`#${rank} Trend in ${regionName}`)
      return _(msg`#${rank} Trend`)
    },
    [_],
  )
}

/**
 * Accent colours for each `activityStatus` value. Surfaced on
 * `HeroTrendCard`'s category row and `TrendSearchHeader`. Values are locked
 * by design.
 */
export const ACTIVITY_STATUS_COLORS: Record<TrendActivityStatus, string> = {
  news_media_trend: '#fbd89d',
  social_media_trend: '#FB9D9F',
  people_are_searching_for: '#9CCCFF',
}

/**
 * Humanize a raw category value the client has no translation for (e.g. a new
 * backend category like "health"): "health" → "Health".
 */
export function formatCategoryLabel(category: string): string {
  if (!category) return category
  return category.charAt(0).toUpperCase() + category.slice(1)
}

/**
 * Localised display labels for category enum + activityStatus enum. Used by
 * every trend card variant. Single source of truth — never inline these maps
 * in a card again.
 *
 * The hook shape (rather than a static export) is required because the
 * underlying `msg` macro returns a `MessageDescriptor` that has to be
 * translated through `useLingui()._` at render time.
 */
export function useTrendLabels() {
  const {_} = useLingui()
  // Category === interest, so the chip/card labels reuse the interest display
  // names (single source of truth in `src/lib/interests.ts`).
  const categoryLabels = useInterestsDisplayNames()
  return useMemo(() => {
    return {
      categoryLabels,
      // Always use this to render a category — it falls back to a humanized
      // raw value so an unmapped category still shows on the cards instead of
      // rendering blank. Lower-cases the key so a capitalized backend value
      // ("Culture") still hits the translated map.
      getCategoryLabel: (category: Trend['category']) => {
        const key =
          typeof category === 'string' ? category.toLowerCase() : category
        return categoryLabels[key] ?? formatCategoryLabel(key)
      },
      activityStatusLabels: {
        news_media_trend: _(msg`News media trend`),
        social_media_trend: _(msg`Social media trend`),
        people_are_searching_for: _(msg`People are searching for`),
      } satisfies Record<TrendActivityStatus, string>,
    }
  }, [_, categoryLabels])
}
